// Prazo de retorno da solicitação de cancelamento (#5827, projeto 11).
//
// O protocolo e a data de retorno nascem no banco (trigger
// fn_solicitacao_cancelamento_protocolo, migration 20260916190000). Aqui é só
// a leitura para a tela: em que pé está o prazo prometido ao cliente.
//
// Compara datas de calendário, sem contar dia útil: a data de retorno já veio
// calculada com os feriados do banco, e recalcular aqui com outra lista de
// feriados (a régua do agrupamento soma GO e Morrinhos) daria divergência.
//
// Sem I/O e sem Date.now(): "hoje" entra por parâmetro.

import type { OrigemSolicitacao, StatusSolicitacaoCancelamento } from './cancelamentos';

export type EstadoPrazoRetorno = 'sem_prazo' | 'respondida' | 'no_prazo' | 'vence_hoje' | 'vencida';

export interface EntradaPrazoRetorno {
  dataRetornoPrevista: string | null | undefined;
  status: StatusSolicitacaoCancelamento;
  validadoEm: string | null | undefined;
}

const soData = (v: string | null | undefined): string | null => {
  const t = String(v ?? '').trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : null;
};

/** "Hoje" no fuso de Brasília, em YYYY-MM-DD. O UTC vira o dia às 21h. */
export function hojeEmBrasilia(agora: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(agora);
}

/**
 * Até a tarefa 07 (mensagem de retorno) existir, conta como respondida a
 * solicitação validada ou encerrada (decisão de 16/09/2026).
 */
export function estadoPrazoRetorno(e: EntradaPrazoRetorno, hoje: string): EstadoPrazoRetorno {
  if (e.status === 'concluida' || e.status === 'cancelada' || soData(e.validadoEm)) return 'respondida';
  const retorno = soData(e.dataRetornoPrevista);
  const dia = soData(hoje);
  if (!retorno || !dia) return 'sem_prazo';
  if (retorno > dia) return 'no_prazo';
  if (retorno === dia) return 'vence_hoje';
  return 'vencida';
}

/** Dias corridos de atraso (0 quando não venceu ou a data é inválida). */
export function diasDeAtraso(dataRetornoPrevista: string | null | undefined, hoje: string): number {
  const retorno = soData(dataRetornoPrevista);
  const dia = soData(hoje);
  if (!retorno || !dia || retorno >= dia) return 0;
  return Math.round((Date.parse(`${dia}T00:00:00Z`) - Date.parse(`${retorno}T00:00:00Z`)) / 86400000);
}

export const ORIGEM_SOLICITACAO_LABEL: Record<OrigemSolicitacao, string> = {
  sistema: 'Tela interna',
  importacao: 'Importação',
  portal: 'Portal',
  whatsapp: 'WhatsApp',
};

export const ORIGENS_SOLICITACAO: OrigemSolicitacao[] = ['sistema', 'portal', 'whatsapp', 'importacao'];
